import { zodResolver } from '@hookform/resolvers/zod';
import { CreateUserSchema, UpdateUserSchema, type CreateUser, type UpdateUser } from '@rfleet/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { KeyRound, Pencil, Plus, UserX } from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Can } from '../../app/guards';
import { Badge, Dialog, EmptyState, Field, PageHeader, Pagination, Skeleton, Spinner, Table, Td, Th, useConfirm, useToast } from '../../components/ui';
import { api, qs, type Paginated } from '../../lib/api';
import { useSession } from '../../lib/session';

interface Role {
  id: string;
  code: string;
  nameAr: string;
  nameEn: string;
  isSystem: boolean;
}
interface User {
  id: string;
  email: string | null;
  phone: string | null;
  fullName: string;
  locale: 'AR' | 'EN';
  status: 'ACTIVE' | 'DISABLED' | 'INVITED';
  lastLoginAt: string | null;
  role: Role;
}

const fmtDate = (s: string | null, lang: string) => (s ? new Intl.DateTimeFormat(lang === 'en' ? 'en-EG' : 'ar-EG-u-nu-latn', { dateStyle: 'medium', timeStyle: 'short', timeZone: 'Africa/Cairo' }).format(new Date(s)) : '—');

export function UsersPage() {
  const { t, i18n } = useTranslation();
  const toast = useToast();
  const confirmDialog = useConfirm();
  const qc = useQueryClient();
  const me = useSession((s) => s.user);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState<User | 'new' | null>(null);
  const [resetFor, setResetFor] = useState<User | null>(null);

  const users = useQuery({ queryKey: ['users', page, search], queryFn: () => api<Paginated<User>>(`/users${qs({ page, q: search })}`) });
  const roles = useQuery({ queryKey: ['roles'], queryFn: () => api<Role[]>('/roles') });
  const roleName = (r: Role) => (i18n.language === 'en' ? r.nameEn : r.nameAr);

  const disable = useMutation({
    mutationFn: (id: string) => api(`/users/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      toast.success(t('common.saved'));
      void qc.invalidateQueries({ queryKey: ['users'] });
    },
    onError: toast.error,
  });

  return (
    <div className="flex flex-col gap-4">
      <PageHeader
        title={t('settings.users.title')}
        actions={
          <Can perm="users:create">
            <button className="btn-primary" onClick={() => setEditing('new')}>
              <Plus size={16} aria-hidden /> {t('settings.users.add')}
            </button>
          </Can>
        }
      />
      <input className="input max-w-sm" placeholder={t('common.search')} value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }} aria-label={t('common.search')} />

      {users.isLoading ? (
        <Skeleton className="h-64" />
      ) : !users.data?.data.length ? (
        <div className="card"><EmptyState title={t('common.empty')} /></div>
      ) : (
        <Table>
          <thead>
            <tr>
              <Th>{t('settings.users.name')}</Th>
              <Th>{t('login.email')}</Th>
              <Th>{t('settings.users.role')}</Th>
              <Th>{t('settings.users.status')}</Th>
              <Th>{t('settings.users.lastLogin')}</Th>
              <Th className="w-1" sticky="end" />
            </tr>
          </thead>
          <tbody>
            {users.data.data.map((u) => (
              <tr key={u.id} className="group hover:bg-surface-hover">
                <Td className="font-semibold">{u.fullName}{u.id === me?.id ? <span className="ms-2 text-xs text-text-faint">({t('settings.users.you')})</span> : null}</Td>
                <Td><span dir="ltr">{u.email}</span></Td>
                <Td>{roleName(u.role)}</Td>
                <Td><Badge tone={u.status === 'ACTIVE' ? 'success' : u.status === 'INVITED' ? 'info' : 'neutral'} dot>{t(`settings.users.statuses.${u.status}`)}</Badge></Td>
                <Td className="text-text-muted">{fmtDate(u.lastLoginAt, i18n.language)}</Td>
                <Td sticky="end">
                  <div className="flex justify-end gap-1">
                    <Can perm="users:update">
                      <button className="btn-ghost size-9 !p-0" title={t('common.edit')} aria-label={t('common.edit')} onClick={() => setEditing(u)}><Pencil size={16} /></button>
                      <button className="btn-ghost size-9 !p-0" title={t('settings.users.resetPassword')} aria-label={t('settings.users.resetPassword')} onClick={() => setResetFor(u)}><KeyRound size={16} /></button>
                    </Can>
                    <Can perm="users:delete">
                      {u.id !== me?.id && u.status === 'ACTIVE' ? (
                        <button className="btn-ghost size-9 !p-0 text-danger-fg" title={t('settings.users.disable')} aria-label={t('settings.users.disable')} onClick={() => confirmDialog(t('settings.users.disableConfirm', { name: u.fullName }), { danger: true }).then((ok) => ok && disable.mutate(u.id))}><UserX size={16} /></button>
                      ) : null}
                    </Can>
                  </div>
                </Td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr><td colSpan={6}><Pagination page={users.data.meta.page} pageSize={users.data.meta.pageSize} total={users.data.meta.total} onPage={setPage} /></td></tr>
          </tfoot>
        </Table>
      )}

      {editing ? <UserDialog user={editing === 'new' ? null : editing} roles={roles.data ?? []} onClose={() => setEditing(null)} /> : null}
      {resetFor ? <ResetPasswordDialog user={resetFor} onClose={() => setResetFor(null)} /> : null}
    </div>
  );
}

function UserDialog({ user, roles, onClose }: { user: User | null; roles: Role[]; onClose: () => void }) {
  const { t, i18n } = useTranslation();
  const toast = useToast();
  const qc = useQueryClient();
  const isNew = !user;
  const form = useForm<CreateUser & UpdateUser>({
    resolver: zodResolver(isNew ? CreateUserSchema : UpdateUserSchema),
    defaultValues: isNew ? { locale: 'AR' } : { fullName: user.fullName, phone: user.phone ?? '', roleId: user.role.id, locale: user.locale, status: user.status },
  });
  const save = useMutation({
    mutationFn: (v: CreateUser & UpdateUser) => (isNew ? api('/users', { method: 'POST', json: v }) : api(`/users/${user.id}`, { method: 'PATCH', json: { ...v, phone: v.phone || null } })),
    onSuccess: () => {
      toast.success(t('common.saved'));
      void qc.invalidateQueries({ queryKey: ['users'] });
      onClose();
    },
    onError: toast.error,
  });
  const err = form.formState.errors as Record<string, { message?: string } | undefined>;
  return (
    <Dialog open onClose={onClose} title={isNew ? t('settings.users.add') : t('settings.users.edit')} footer={
      <>
        <button className="btn-secondary" onClick={onClose}>{t('common.cancel')}</button>
        <button className="btn-primary" form="user-form" type="submit" disabled={save.isPending}>{save.isPending ? <Spinner /> : null}{t('common.save')}</button>
      </>
    }>
      <form id="user-form" onSubmit={form.handleSubmit((v) => save.mutate(v))} className="grid gap-4 sm:grid-cols-2" noValidate>
        <Field label={t('settings.users.name')} required error={err['fullName']?.message}>{(id) => <input id={id} className="input" {...form.register('fullName')} />}</Field>
        {isNew ? <Field label={t('login.email')} required error={err['email']?.message}>{(id) => <input id={id} type="email" dir="ltr" className="input" {...form.register('email')} />}</Field> : null}
        <Field label={t('settings.users.phone')} error={err['phone']?.message}>{(id) => <input id={id} dir="ltr" className="input" placeholder="01xxxxxxxxx" {...form.register('phone')} />}</Field>
        {isNew ? <Field label={t('login.password')} required error={err['password']?.message} hint={t('settings.users.passwordHint')}>{(id) => <input id={id} type="password" dir="ltr" className="input" autoComplete="new-password" {...form.register('password')} />}</Field> : null}
        <Field label={t('settings.users.role')} required error={err['roleId']?.message}>
          {(id) => (
            <select id={id} className="input" {...form.register('roleId')}>
              <option value="">—</option>
              {roles.filter((r) => r.code !== 'DRIVER').map((r) => <option key={r.id} value={r.id}>{i18n.language === 'en' ? r.nameEn : r.nameAr}</option>)}
            </select>
          )}
        </Field>
        <Field label={t('settings.company.locale')}>{(id) => <select id={id} className="input" {...form.register('locale')}><option value="AR">العربية</option><option value="EN">English</option></select>}</Field>
        {!isNew ? <Field label={t('settings.users.status')}>{(id) => <select id={id} className="input" {...form.register('status')}><option value="ACTIVE">{t('settings.users.statuses.ACTIVE')}</option><option value="DISABLED">{t('settings.users.statuses.DISABLED')}</option></select>}</Field> : null}
      </form>
    </Dialog>
  );
}

function ResetPasswordDialog({ user, onClose }: { user: User; onClose: () => void }) {
  const { t } = useTranslation();
  const toast = useToast();
  const [pw, setPw] = useState('');
  const save = useMutation({
    mutationFn: () => api(`/users/${user.id}/reset-password`, { method: 'POST', json: { newPassword: pw } }),
    onSuccess: () => { toast.success(t('common.saved')); onClose(); },
    onError: toast.error,
  });
  return (
    <Dialog open onClose={onClose} title={`${t('settings.users.resetPassword')} — ${user.fullName}`} footer={
      <>
        <button className="btn-secondary" onClick={onClose}>{t('common.cancel')}</button>
        <button className="btn-primary" disabled={pw.length < 8 || save.isPending} onClick={() => save.mutate()}>{save.isPending ? <Spinner /> : null}{t('common.save')}</button>
      </>
    }>
      <Field label={t('settings.users.newPassword')} required hint={t('settings.users.passwordHint')}>
        {(id) => <input id={id} type="password" dir="ltr" className="input" value={pw} onChange={(e) => setPw(e.target.value)} autoComplete="new-password" />}
      </Field>
    </Dialog>
  );
}
